// src/pages/Landing.jsx
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styles from './Landing.module.scss';
import LandingHero from '../components/LandingHero/LandingHero';
import LandingFeature from '../components/LandingFeature/LandingFeature';
import ModeloDestacado from '../components/ModeloDestacado/ModeloDestacado';
import LandingForm from '../components/LandingForm/LandingForm';
import Cotizador from '../components/Cotizador/Cotizador';
import PageFooter from '../components/PageFooter';
import WhatsappComponent from '../components/WhatsappComponent/WhatsappComponent';
import featureContent from '../components/LandingFeature/featureContent.json';

const countryConfig = {
  Perú: {
    title: 'Zoomlion Latinoamérica | Perú',
    description: 'Conoce las ofertas de Zoomlion Perú',
    whatsappComponent: <WhatsappComponent country="Perú" />,
    catalogueLink: 'https://heyzine.com/flip-book/f34195a0ca.html#page/1',
    privacyPolicy: 'https://zoomlionlatam.com/privacidad/privacidad_peru.pdf'
  },
};

const imageList = [
  '/images/Landing/grua.webp',
  '/images/Landing/agricultura.webp',
  '/images/Landing/mineria.webp',
  '/images/Landing/construccion.webp'
];

const Landing = () => {
  const location = useLocation();
  const isColombia = location.pathname.includes('colombia');
  const currentCountry = isColombia ? 'Colombia' : 'Perú';
  const config = countryConfig[currentCountry];

  const [isCotizadorOpen, setIsCotizadorOpen] = useState(false);
  const openCotizador = () => setIsCotizadorOpen(true);
  const closeCotizador = () => setIsCotizadorOpen(false);

  return (
    <>
      <Helmet>
        <title>{config.title}</title>
        <meta name="description" content={config.description} />
        <link rel="canonical" href={window.location.href} />

        <meta property="og:title" content={config.title} />
        <meta property="og:description" content={config.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:image" content="/images/banner.png" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={config.title} />
        <meta name="twitter:description" content={config.description} />
        <meta name="twitter:image" content="/images/banner.png" />

        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'Zoomlion Latinoamérica',
            url: window.location.href,
            logo: `${window.location.origin}/images/landing/banner.png`,
            sameAs: [
              'https://www.facebook.com/zoomliontech',
              'https://www.instagram.com/zoomlionlatam/',
              'https://www.linkedin.com/company/zoomlion-latam/',
              'https://www.youtube.com/@zoomlionglobal706'
            ]
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Inicio',
                item: window.location.origin
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Productos',
                item: `${window.location.origin}/productos`
              }
            ]
          })}
        </script>
      </Helmet>

      <div className={styles['landing-peru-page']}>
        {config.whatsappComponent}

        <LandingHero
          currentCountry={currentCountry}
          catalogueLink={config.catalogueLink}
          onOpenCotizador={openCotizador}
          imageList={imageList}
        />

        <div className={styles['landing-features-wrapper']}>
          {Object.keys(featureContent).map((key, idx) => (
            <LandingFeature key={key} type={key} index={idx} />
          ))}
        </div>

        <ModeloDestacado />

        <a href="/productos" className={styles['landing-page-ver-todos']}>
          <div className={styles['landing-page-ver-todos-text']}>
            Ver todos los productos
          </div>
        </a>

        <LandingForm
          country={currentCountry}
          privacyPolicy={config.privacyPolicy}
        />

        {isCotizadorOpen && (
          <Cotizador currentCountry={currentCountry} onClose={closeCotizador} />
        )}

        <div className={styles['landing-peru-page-footer']}>
          <PageFooter country={currentCountry} />
        </div>
      </div>
    </>
  );
};

export default Landing;
